function initDescriptionDrawers(root = document) {
  root.querySelectorAll('[data-hd-pdp-tools]').forEach((tools) => {
    if (tools.dataset.hdDescriptionBound === 'true') return;
    tools.dataset.hdDescriptionBound = 'true';

    const dialog = tools.querySelector('[data-hd-description-dialog]');
    const open = tools.querySelector('[data-hd-description-open]');
    const close = tools.querySelector('[data-hd-description-close]');
    if (!dialog || !open) return;

    const panel = dialog.querySelector('.hd-description-drawer__panel');
    let closeTimer = null;

    const finishClose = () => {
      window.clearTimeout(closeTimer);
      dialog.classList.remove('is-open', 'is-closing');
      if (dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    };

    const requestClose = () => {
      if (dialog.classList.contains('is-closing')) return;
      dialog.classList.remove('is-open');
      dialog.classList.add('is-closing');

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion || !panel) {
        finishClose();
        return;
      }

      const onTransitionEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') return;
        panel.removeEventListener('transitionend', onTransitionEnd);
        finishClose();
      };

      panel.addEventListener('transitionend', onTransitionEnd);
      closeTimer = window.setTimeout(() => {
        panel.removeEventListener('transitionend', onTransitionEnd);
        finishClose();
      }, 520);
    };

    open.addEventListener('click', () => {
      window.clearTimeout(closeTimer);
      dialog.classList.remove('is-closing');
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      window.requestAnimationFrame(() => dialog.classList.add('is-open'));
    });

    close?.addEventListener('click', requestClose);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) requestClose();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      requestClose();
    });
  });
}

function initUpsellControls(root = document) {
  root.querySelectorAll('[data-hd-product-upsell]').forEach((section) => {
    if (section.dataset.hdUpsellBound === 'true') return;
    section.dataset.hdUpsellBound = 'true';

    const rail = section.querySelector('[data-hd-product-upsell-rail]');
    const previous = section.querySelector('[data-hd-upsell-prev]');
    const next = section.querySelector('[data-hd-upsell-next]');
    if (!rail) return;

    const scrollByCard = (direction) => {
      const card = rail.querySelector('.hd-product-card');
      const amount = card ? card.getBoundingClientRect().width + 16 : 280;
      rail.scrollBy({ left: amount * direction, behavior: 'smooth' });
    };

    previous?.addEventListener('click', () => scrollByCard(-1));
    next?.addEventListener('click', () => scrollByCard(1));
  });
}

function initPdpMediaHover(root = document) {
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!canHover || reducedMotion) return;

  root.querySelectorAll('[data-hd-pdp-hover-media]').forEach((media) => {
    if (media.dataset.hdHoverBound === 'true') return;
    if (media.closest('zoom-dialog, .dialog-zoomed-gallery')) return;
    if (media.closest('.product-card, .product-grid__card, .resource-card, .card-gallery')) return;

    const image = media.querySelector('.product-media__image');
    if (!image) return;

    media.dataset.hdHoverBound = 'true';
    const zoomScale = 1.18;

    const state = {
      active: false,
      raf: 0,
      scale: 1,
      x: 0,
      y: 0,
      originX: 50,
      originY: 50,
      targetScale: 1,
      targetX: 0,
      targetY: 0,
      targetOriginX: 50,
      targetOriginY: 50,
    };

    const render = () => {
      const scaleEase = state.active ? 0.12 : 0.08;
      const panEase = state.active ? 0.16 : 0.09;

      state.scale += (state.targetScale - state.scale) * scaleEase;
      state.x += (state.targetX - state.x) * panEase;
      state.y += (state.targetY - state.y) * panEase;
      state.originX += (state.targetOriginX - state.originX) * panEase;
      state.originY += (state.targetOriginY - state.originY) * panEase;

      const scaleDone = Math.abs(state.targetScale - state.scale) < 0.001;
      const xDone = Math.abs(state.targetX - state.x) < 0.05;
      const yDone = Math.abs(state.targetY - state.y) < 0.05;
      const originXDone = Math.abs(state.targetOriginX - state.originX) < 0.05;
      const originYDone = Math.abs(state.targetOriginY - state.originY) < 0.05;

      image.style.setProperty('--hd-pdp-hover-scale', state.scale.toFixed(4));
      image.style.setProperty('--hd-pdp-hover-x', `${state.x.toFixed(2)}px`);
      image.style.setProperty('--hd-pdp-hover-y', `${state.y.toFixed(2)}px`);
      image.style.setProperty('--hd-pdp-hover-origin-x', `${state.originX.toFixed(2)}%`);
      image.style.setProperty('--hd-pdp-hover-origin-y', `${state.originY.toFixed(2)}%`);

      if (scaleDone && xDone && yDone && originXDone && originYDone) {
        state.scale = state.targetScale;
        state.x = state.targetX;
        state.y = state.targetY;
        state.originX = state.targetOriginX;
        state.originY = state.targetOriginY;

        if (!state.active) {
          image.style.willChange = '';
          image.style.removeProperty('--hd-pdp-hover-scale');
          image.style.removeProperty('--hd-pdp-hover-x');
          image.style.removeProperty('--hd-pdp-hover-y');
          image.style.removeProperty('--hd-pdp-hover-origin-x');
          image.style.removeProperty('--hd-pdp-hover-origin-y');
          state.raf = 0;
          return;
        }

        state.raf = 0;
        return;
      }

      state.raf = window.requestAnimationFrame(render);
    };

    const updateTargetFromPointer = (event) => {
      const rect = media.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const xRatio = Math.max(0, Math.min((event.clientX - rect.left) / rect.width, 1));
      const yRatio = Math.max(0, Math.min((event.clientY - rect.top) / rect.height, 1));
      const originX = Math.max(6, Math.min(xRatio * 100, 94));
      const originY = Math.max(6, Math.min(yRatio * 100, 94));
      const maxPanX = Math.min(rect.width * (zoomScale - 1) * 0.62, 96);
      const maxPanY = Math.min(rect.height * (zoomScale - 1) * 0.62, 76);

      state.targetOriginX = originX;
      state.targetOriginY = originY;
      state.targetX = (0.5 - xRatio) * maxPanX;
      state.targetY = (0.5 - yRatio) * maxPanY;
      state.targetScale = zoomScale;
    };

    const start = (event) => {
      if (event.pointerType === 'touch') return;
      state.active = true;
      media.classList.add('is-hd-media-hovering');
      updateTargetFromPointer(event);
      image.style.willChange = 'transform, transform-origin';
      if (!state.raf) state.raf = window.requestAnimationFrame(render);
    };

    const move = (event) => {
      if (event.pointerType === 'touch') return;
      updateTargetFromPointer(event);
      if (!state.raf) state.raf = window.requestAnimationFrame(render);
    };

    const end = () => {
      state.active = false;
      state.targetScale = 1;
      state.targetX = 0;
      state.targetY = 0;
      state.targetOriginX = 50;
      state.targetOriginY = 50;
      media.classList.remove('is-hd-media-hovering');
      if (!state.raf) state.raf = window.requestAnimationFrame(render);
    };

    media.addEventListener('pointerenter', start);
    media.addEventListener('pointermove', move);
    media.addEventListener('pointerleave', end);
    media.addEventListener('pointercancel', end);
  });
}

function initPdpConversion(root = document) {
  initDescriptionDrawers(root);
  initUpsellControls(root);
  initPdpMediaHover(root);
}

initPdpConversion();
document.addEventListener('shopify:section:load', (event) => initPdpConversion(event.target));
